import { ReactElement, useEffect, useRef, useState } from "react";
import AcquisitionChart from "../../../../interruptions/dashboard/wrappers/acquisitionChart";
import { DashboardSchemaType, IDashboardResult } from "../../../../interruptions/types";
import { IDictionary, IParentSize, IRealtimeData, ISelectedNode, IState, Modules } from "../../../../../types";
import { IAcquisitionChartConfig, IDashboard, IDashboardComponent } from "../../../types";
import { useAsyncFetch } from "../../../../../system/useFetch";
import { useSelector } from "react-redux";
import { paletteController } from "../../../../interruptions/interruptionDashboard";
import { DateTime } from "luxon";
import { formatTimeWithSeconds, getFloatValue } from "../../../../../system/utils";

interface IProps {
  realtime: Record<string, IRealtimeData>;
  component: IDashboardComponent<IAcquisitionChartConfig>;
  parentSize: IParentSize;
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  runtime: boolean;
  schemas: Record<DashboardSchemaType, any>;
}

interface IPoint {
  x: number;
  y: number;
}

interface ITemporalData {
  last: DateTime | null;
  variables: Record<string, IPoint[]>;
}

const AcquisitionChartWrapper = ({ dashboard, setDashboard, component, parentSize, realtime, runtime, schemas }: IProps): ReactElement => {
  const toolbox = { cursor: false, zoom: false, legend: true };
  const buffer = useRef<ITemporalData>({ last: null, variables: {} });
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ getDashboardData ] = useAsyncFetch<IDashboardResult>();
  const [ responseData, setResponseData] = useState<any>(null);

  useEffect(() => {
    if (!runtime || component.config.acquisitionCell.variables.length === 0){
      return;
    }

    const to = DateTime.now();
    const from = to.plus( { hour: dashboard.customConfig.range ? -dashboard.customConfig.range : -1 } );

    buffer.current.last = to;
    buffer.current.variables = {};

    getDashboardData({
      module: "dashboard",
      query: "getDashboardData",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
        moduleID: Modules.INTERRUPTIONS,
        from: from.toFormat("yyyy-MM-dd HH:mm:ss"),
        to: to.toFormat("yyyy-MM-dd HH:mm:ss"),
        cells: [ component.config.acquisitionCell ]
      }
    }).then((request) => {
      if (!request) {
        setResponseData(null);

        return;
      }

      setResponseData({ ...request[component.config.acquisitionCell.key].result });
    });
  }, [ component, runtime ]);

  useEffect(() => {
    const now = DateTime.now();

    const to = now.toSeconds();
    const from = now.minus( { hour: dashboard.customConfig.range ?? 1 } ).toSeconds();

    try {
      for (const variable of component.config.acquisitionCell.variables) {
        if (!responseData && buffer.current.last) {
          const point = { x: to, y: getFloatValue(realtime[variable.field], Number.MIN_VALUE) };
          if (!buffer.current.variables[variable.hash]) {
            buffer.current.variables[variable.hash] = [point];
          } else {
            (buffer.current.variables[variable.hash] ?? []).push(point);
          }
        } else if (responseData && responseData.variables) {
          const values = responseData.variables[variable.hash] as IPoint[] | undefined;

          if (values) {
            if (buffer.current.last){
              buffer.current.last = null;
              (buffer.current.variables[variable.hash] ?? []).forEach(p => values.push(p));
              buffer.current.variables[variable.hash] = [];
            }

            if (realtime[variable.field]) {
              values.push({ x: to, y: getFloatValue(realtime[variable.field], 0) });
            }

            const len = values.length;
            while(len > 1 && values[1] && values[1].x < from) {
              values.shift();
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [ realtime, responseData ]);

  const onZoomArea = (from: number, to: number): void => {
    console.log(from, to);
    console.log(realtime);
  };

  const onUpdatePalette = (p: IDictionary<string>, hasChange = false) => {
    let processChange = hasChange;
    paletteController.counter--;

    if(paletteController.counter === 0) {
      for (const [ k, v ] of Object.entries(paletteController.palette)) {
        if (!v) {
          delete p[k];
        }
      }

      processChange = true;
    }

    if (processChange){
      setDashboard({ ...dashboard, layout: { ...dashboard.layout, palette: p } });
    }
  };

  if (!schemas || !responseData) {
    return <></>;
  }

  return (
    <AcquisitionChart cell={component.config.acquisitionCell} schemas={schemas}
      data={responseData}
      parentSize={parentSize}
      palette={dashboard.layout.palette} updatePalette={onUpdatePalette}
      toolbox={toolbox} onZoomArea={onZoomArea} fontSize={dashboard.customConfig.fontSize}
      xAxisFormatTick={(value) => formatTimeWithSeconds(value as number)}
      disableLineClass={true} adjustAxes={true}/>
  );
};

export default AcquisitionChartWrapper;
